import * as React from 'react';

import { useAuth } from '@/auth/auth-context';
import { EnvChip } from './env-chip';
import { LogoutButton } from './logout-button';
import { NotificationBell } from './notification-bell';
import { NotificationBanner } from './notification-banner';

// =============================================================================
// topbar.tsx — Barre supérieure (design system) : sticky 56px + backdrop-blur.
// =============================================================================
// DS layout rule : « topbar sticky 56px with backdrop-filter: blur(12px) ». Contient
// la pastille d'environnement (EnvChip), l'identité authentifiée et la déconnexion.
// L'IDENTITÉ admin est PRÉSERVÉE À L'IDENTIQUE depuis l'ancien header (« Administrateur authentifié »
// = subject). CÔTÉ FOURNISSEUR, le libellé accessible est passé de « Tenant authentifié » à
// « Entreprise authentifiée » (P-6, 2026-09-23) : il décrit ce qui est RÉELLEMENT affiché, à savoir la
// raison sociale. Garder l'ancien libellé aurait fait annoncer « tenant » par un lecteur d'écran devant
// un nom d'entreprise — un libellé accessible qui ment sur son contenu est pire qu'absent. Les six
// localisateurs de test qui s'y adossaient ont suivi (app-shell*.test.tsx).

const UUID_PATTERN = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/** Abrège un subject UUID en « 8 premiers…2 derniers » (9b1f8c7e…4f) ; tout autre subject reste intact. */
export function abbreviateSubject(subject: string): string {
  return UUID_PATTERN.test(subject)
    ? `${subject.slice(0, 8)}…${subject.slice(-2)}`
    : subject;
}

export function Topbar(): React.JSX.Element {
  const auth = useAuth();
  // La cloche de notifications et le bandeau temps réel sont FOURNISSEUR uniquement (routes @SupplierRoute,
  // supplier-scopé). L'admin ne les voit pas (aucune notification supplier-scopée pour lui).
  const isSupplier = auth.role === 'supplier';
  return (
    <>
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-4 border-b border-border bg-card/80 px-7 backdrop-blur-md">
      <EnvChip />
      <div className="flex items-center gap-4">
        {isSupplier ? <NotificationBell /> : null}
        {auth.role === 'admin' && auth.subject ? (
          // ADMIN réel (L-3) : identité OIDC (subject), affichage seul (cross-tenant, pas de SIRET).
          <span className="text-sm text-muted-foreground" aria-label="Administrateur authentifié">
            Administrateur&nbsp;
            {auth.name !== undefined ? (
              <span className="text-foreground" title={auth.subject}>
                {auth.name}
              </span>
            ) : (
              // Identifiant abrégé (9b1f8c7e…4f) ; l'UUID complet reste lisible en infobulle.
              <span className="font-mono text-foreground" title={auth.subject}>
                {abbreviateSubject(auth.subject)}
              </span>
            )}
          </span>
        ) : (
          // FOURNISSEUR (ou interim admin sans subject) : l'ENTREPRISE est l'identité affichée.
          // P-6 (2026-09-23) : on lisait « Tenant 53993947000016 ». « Tenant » est un mot d'architecte
          // et un SIRET n'est le nom de personne — l'utilisateur ne s'y reconnaissait pas. On nomme
          // donc l'entreprise, et on ne retombe sur le SIRET que faute de mieux (aucun relevé
          // d'enrichissement), en chasse fixe pour dire qu'il s'agit alors d'un identifiant.
          // Le SIRET reste en infobulle dans les deux cas : c'est lui qu'on donne au support.
          <span
            className="text-sm text-muted-foreground"
            aria-label="Entreprise authentifiée"
          >
            {auth.organisation !== undefined ? (
              <span className="font-medium text-foreground" title={auth.tenantId}>
                {auth.organisation}
              </span>
            ) : (
              <>
                Tenant&nbsp;
                <span className="font-mono text-foreground" title={auth.tenantId}>
                  {auth.tenantId}
                </span>
              </>
            )}
          </span>
        )}
        <LogoutButton />
      </div>
    </header>
    {/* Bandeau in-app silencieux (DEC-31) — sous la barre, fournisseur uniquement. Vide tant qu'aucun
        signal socket n'est reçu (n'occupe alors aucun espace visible). */}
    {isSupplier ? <NotificationBanner /> : null}
    </>
  );
}
